import type { ReactNode } from 'react';
import { useAtom } from 'jotai';
import { Calendar, Loader2 } from 'lucide-react';
import { revenueDateRangeAtom } from '@/store/revenue';

interface RevenueDateRangeBarProps {
  isLoading: boolean;
  onLoad: () => void;
  // Called whenever a date changes, so the page can wait for "Load Data" again.
  onRangeChange: () => void;
  footer?: ReactNode;
}

/** Start/end date pickers and the "Load Data" button every revenue page starts with. */
export function RevenueDateRangeBar({ isLoading, onLoad, onRangeChange, footer }: RevenueDateRangeBarProps) {
  const [range, setRange] = useAtom(revenueDateRangeAtom);

  const dateInputs = [
    { label: 'Start Date', key: 'startDate' as const },
    { label: 'End Date', key: 'endDate' as const },
  ];

  return (
    <div className="adv-card p-6">
      <div className="flex flex-wrap items-end gap-4">
        {dateInputs.map(({ label, key }) => (
          <div key={key}>
            <label className="adv-field-label">{label}</label>
            <div className="relative">
              <Calendar
                className="absolute left-3 top-1/2 -translate-y-1/2 text-adv-tertiary pointer-events-none"
                size={18}
              />
              <input
                type="date"
                value={range[key]}
                onChange={(e) => {
                  setRange({ ...range, [key]: e.target.value });
                  onRangeChange();
                }}
                className="adv-input adv-num pl-10 pr-4"
              />
            </div>
          </div>
        ))}

        <button
          onClick={onLoad}
          disabled={isLoading}
          className="adv-btn adv-btn--primary h-11"
        >
          {isLoading ? (
            <>
              <Loader2 className="animate-spin" size={18} />
              Loading...
            </>
          ) : (
            'Load Data'
          )}
        </button>
      </div>

      {footer && <p className="mt-4 text-adv-body-s text-adv-secondary">{footer}</p>}
    </div>
  );
}
